import { createDraftEntry } from '@/utils/priceListDraftQueue';
import { parseNumericInput } from '@/utils/priceListProductCalculator';
import { getProductMainCategories } from '@/data/productCategories';

const COMPOUND_NAME_SEPARATOR = ' + ';
export const SISTEMA_MANUFACTURER_CODE = '00';
export const SISTEMA_MANUFACTURER_NAME = 'Sistema';

/**
 * Minimal update payload — does NOT include pricing_detail, so legacy products stay legacy.
 * @param {Object} params
 * @param {Object} params.item
 * @param {number|string} params.newPrice
 * @param {string} [params.productName]
 * @param {Array<string>} params.categories
 * @param {Array<Object>} params.priceListIds
 * @returns {Object}
 */
export function createSimplePriceUpdatePayload({
    item,
    newPrice,
    productName,
    categories,
    priceListIds,
}) {
    return {
        old_product_code: item.product_code,
        product_code: item.product_code,
        product_name: productName ?? item.product_name,
        measurement_unit: item.measurement_unit,
        price: parseNumericInput(newPrice ?? item.price),
        categories: [...(categories || [])],
        price_list_ids: (priceListIds || []).map((priceList) => ({
            id: priceList.id ? priceList.id : priceList,
        })),
    };
}

/**
 * @param {Object} params
 * @param {Object} params.item
 * @param {number|string} params.newPrice
 * @param {string} [params.productName]
 * @param {Array<string>} params.categories
 * @param {Array<Object>} params.priceListIds
 * @returns {Object}
 */
/**
 * Merges a pending list price from the update panel or a simple draft into a product
 * item so the builder shows the updated reference and final list prices.
 * @param {Object} item
 * @param {number|string|null|undefined} price
 * @param {{ productName?: string }} [options]
 * @returns {Object}
 */
export function applyPendingListPriceToItem(item, price, { productName } = {}) {
    const numericPrice = parseNumericInput(price);
    if (!item || numericPrice <= 0) {
        return item;
    }

    const next = {
        ...item,
        price: numericPrice,
    };
    const trimmedName = String(productName || '').trim();
    if (trimmedName) {
        next.product_name = trimmedName;
    }
    if (item.pricing_detail) {
        next.pricing_detail = {
            ...item.pricing_detail,
            listPriceRonIncVatReference: numericPrice,
        };
    }
    return next;
}

/**
 * Resolves the effective pending price/name from an update-panel row and optional draft.
 * @param {Object} row
 * @param {Object|null} [draft]
 * @returns {{ price: number, productName?: string }|null}
 */
export function resolvePendingUpdateFromRow(row, draft = null) {
    if (!row) {
        return null;
    }

    const rowPrice = parseNumericInput(row.new_price);
    const draftPrice = parseNumericInput(draft?.payload?.price);
    const price = rowPrice > 0 ? rowPrice : draftPrice;

    if (price <= 0) {
        return null;
    }

    const productName = String(row.new_name || draft?.payload?.product_name || '').trim();
    return productName ? { price, productName } : { price };
}

export function createSimplePriceUpdateDraft({
    item,
    newPrice,
    productName,
    categories,
    priceListIds,
}) {
    const payload = createSimplePriceUpdatePayload({
        item,
        newPrice,
        productName,
        categories,
        priceListIds,
    });

    return createDraftEntry({
        action: 'update',
        payload,
        builderState: null,
        sourceProductCode: item.product_code,
        previousPrice: item.price,
        updateMode: 'simple',
    });
}

/**
 * Builds compound product defaults from selected price-list products.
 * @param {Array<Object>} products
 * @returns {Object|null}
 */
export function buildCompoundProductSeed(products) {
    if (!Array.isArray(products) || products.length < 2) {
        return null;
    }

    const componentCodes = products.map((product) => product.product_code);
    const names = products.map((product) => String(product.product_name || product.name || '').trim());
    const priceSum = products.reduce(
        (sum, product) => sum + (parseNumericInput(product.price) || 0),
        0,
    );

    const categorySet = new Set();
    for (const product of products) {
        for (const category of getProductMainCategories(product)) {
            categorySet.add(category);
        }
    }

    const units = products.map((product) => product.measurement_unit).filter(Boolean);
    const measurementUnit = units.length && units.every((unit) => unit === units[0])
        ? units[0]
        : 'set';

    return {
        productName: names.filter(Boolean).join(COMPOUND_NAME_SEPARATOR),
        price: Math.round(priceSum * 100) / 100,
        measurementUnit,
        categories: [...categorySet],
        componentCodes,
        manufacturerCode: SISTEMA_MANUFACTURER_CODE,
        manufacturerName: SISTEMA_MANUFACTURER_NAME,
    };
}
